<template>
  <div class="login-body-polished">
    <div class="container">
      <!-- Left Panel - Branding & Info -->
      <div class="left-panel">
        <div class="brand-content">
          <div class="logo-container">
            <div class="logo">
              <img src="@/assets/logo.png" alt="BidSnap AI Logo" width="56" height="56" class="logo-style">
            </div>
            <h1 class="brand-name">BidSnap AI</h1>
          </div>

          <div class="hero-content text-left">
            <h2 class="hero-title text-left">Join the future of bidding intelligence</h2>
            <p class="hero-description text-left">Create your account to unlock AI-powered tender discovery, eligibility
              analysis, and predictive success analytics.</p>

            <div class="features">
              <!-- Feature 1 -->
              <div class="feature-item">
                <div class="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" stroke="currentColor" stroke-width="2"
                      stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="block mb-2px" style="font-weight: 600;">Instant access</div>
                  <div class="text-sm opacity-90 leading-tight">Start discovering relevant tenders immediately after
                    registration.</div>
                </div>
              </div>

              <!-- Feature 2 -->
              <div class="feature-item">
                <div class="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M9 12L11 14L15 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="block mb-2px" style="font-weight: 600;">Secure & reliable</div>
                  <div class="text-sm opacity-90 leading-tight">Your data is encrypted and protected with
                    enterprise-grade security.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="decorative-elements">
          <div class="blob blob-1"></div>
          <div class="blob blob-2"></div>
          <div class="blob blob-3"></div>
        </div>
      </div>

      <!-- Right Panel - Registration Form -->
      <div class="right-panel">
        <div class="login-container">
          <div class="login-header text-left">
            <h2 class="login-title text-left">Create your account</h2>
            <p class="login-subtitle text-left">Fill in your details to get started with BidSnap AI.</p>
          </div>

          <form class="login-form" @submit.prevent="registerUser">

            <!-- Full Name -->
            <div class="form-group">
              <label for="name" class="form-label block text-left">Full Name</label>
              <div class="input-wrapper">
                <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  <path
                    d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <input type="text" id="name" class="form-input" placeholder="Your Name" v-model.trim="userProfile.name"
                  :class="{ 'error-input': errors.name }">
              </div>
              <p v-if="errors.name" class="error-msg">{{ errors.name }}</p>
            </div>

            <!-- Email -->
            <div class="form-group">
              <label for="email" class="form-label block text-left">Email address</label>
              <div class="input-wrapper">
                <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M22 6L12 13L2 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
                <input type="email" id="email" class="form-input" placeholder="you@example.com"
                  v-model.trim="userProfile.email" :class="{ 'error-input': errors.email }">
              </div>
              <p v-if="errors.email" class="error-msg">{{ errors.email }}</p>
            </div>

            <!-- Mobile + OTP -->
            <div class="form-group">
              <label for="mobile" class="form-label block text-left">Mobile Number</label>
              <div class="mobile-row">
                <!-- Custom Country Code Picker -->
                <div class="cc-picker" v-click-outside="closeCountryDropdown">
                  <button type="button" class="cc-trigger" :disabled="otpVerified" @click="toggleCountryDropdown">
                    <span class="cc-flag">{{ selectedCountry.flag }}</span>
                    <span class="cc-code">+{{ countryCode }}</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                      :style="{ transform: ccDropdownOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  <div v-if="ccDropdownOpen" class="cc-dropdown">
                    <div class="cc-search-wrap">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      <input ref="ccSearchInput" v-model="ccSearch" type="text" class="cc-search"
                        placeholder="Search country..." @click.stop />
                    </div>
                    <ul class="cc-list">
                      <li v-for="c in filteredCountryCodes" :key="c.id" class="cc-item"
                        :class="{ active: countryId === c.id }" @click="selectCountry(c)">
                        <span class="cc-item-flag">{{ c.flag }}</span>
                        <span class="cc-item-name">{{ c.name }}</span>
                        <span class="cc-item-code">+{{ c.code }}</span>
                      </li>
                      <li v-if="filteredCountryCodes.length === 0" class="cc-empty">No results</li>
                    </ul>
                  </div>
                </div>

                <!-- Mobile Input with Verified Badge -->
                <div class="mobile-input-wrapper">
                  <input type="tel" id="mobile" class="mobile-input" v-model.trim="userProfile.mobile"
                    @input="userProfile.mobile = userProfile.mobile.replace(/[^0-9]/g, ''); resetOtpState();"
                    maxlength="15" inputmode="numeric" autocomplete="off" placeholder="Mobile number"
                    :class="{ 'error-input': errors.mobile, 'verified': otpVerified }" :disabled="otpVerified" />
                  <!-- Verified Checkmark Badge (circular, WhatsApp style) -->
                  <Transition name="badge-pop">
                    <div v-if="otpVerified" class="verified-badge-circle">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3.5"
                        stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                  </Transition>
                </div>

                <!-- OTP / Change Button -->
                <button v-if="!otpVerified" type="button" class="otp-send-btn"
                  :disabled="otpSending || userProfile.mobile.length < 7 || resendTimer > 0 || resendCount >= maxResends"
                  @click="sendOtp"
                  :title="resendCount >= maxResends ? `Max ${maxResends} resends reached. Wait 10 min.` : ''">
                  <span v-if="otpSending" class="spinner-sm-dark"></span>
                  <span v-else-if="otpSent && resendTimer > 0">{{ resendTimer }}s</span>
                  <span v-else-if="otpSent && resendCount >= maxResends" style="font-size: 0.75rem;">Max resends</span>
                  <span v-else-if="otpSent">Resend ({{ resendCount }}/{{ maxResends }})</span>
                  <span v-else>Send OTP</span>
                </button>

                <!-- Change Button (when verified) -->
                <button v-else type="button" class="change-btn" @click="resetOtpState" title="Change mobile number">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 4v6h6M23 20v-6h-6" />
                    <path d="M20.49 9A9 9 0 0 0 5.64 5.64M3.51 15A9 9 0 0 0 18.36 18.36" />
                  </svg>
                </button>
              </div>
              <p v-if="errors.mobile" class="error-msg">{{ errors.mobile }}</p>
            </div>

            <!-- OTP Input (shown after OTP is sent) -->
            <div class="form-group" v-if="otpSent && !otpVerified">
              <label class="form-label block text-left">Enter OTP</label>
              <p class="otp-hint">
                OTP sent to +{{ countryCode }}{{ userProfile.mobile }}. Valid for 10 minutes.
                <span v-if="otpAttempts > 0 && !otpLocked" style="margin-left: 8px; color: #ea580c;">
                  {{ maxOtpAttempts - otpAttempts }} attempt(s) remaining
                </span>
              </p>
              <div class="otp-boxes-row">
                <input v-for="i in 6" :key="i" :ref="el => { if (el) otpRefs[i - 1] = el }" type="text"
                  inputmode="numeric" maxlength="1" class="otp-box" :class="{ 'error-input': errors.otp || otpLocked }"
                  :value="otpDigits[i - 1]" :disabled="otpLocked" @input="onOtpDigitInput(i - 1, $event)"
                  @keydown="onOtpKeydown(i - 1, $event)" @paste="onOtpPaste($event)" />
                <button type="button" class="otp-send-btn" style="background:#16a34a;"
                  :disabled="otpVerifying || otpDigits.join('').length !== 6 || otpLocked" @click="verifyOtp">
                  <span v-if="otpVerifying" class="spinner-sm-dark"></span>
                  <span v-else>Verify</span>
                </button>
              </div>
              <p v-if="otpLocked" class="error-msg" style="color: #dc2626; font-weight: 600;">
                Maximum attempts reached. Please request a new OTP.
              </p>
              <p v-else-if="errors.otp" class="error-msg">{{ errors.otp }}</p>
            </div>

            <!-- License Code -->
            <div class="form-group">
              <label for="license" class="form-label block text-left">License Code</label>
              <div class="input-wrapper">
                <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 2L19 4M19 4L17 6M19 4L21 6M19 4L17 2" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" />
                  <path
                    d="M15 7L8.7 13.3C8.3 13.7 8.1 14.2 8.1 14.8V17.2C8.1 17.6 8.5 18 8.9 18H11.3C11.8 18 12.3 17.8 12.7 17.4L19 11.1"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M3 21H21" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
                <input type="text" id="license" class="form-input" placeholder="XXXX-XXXX-XXXX"
                  v-model.trim="userProfile.license" :class="{ 'error-input': errors.license }">
              </div>
              <p v-if="errors.license" class="error-msg">{{ errors.license }}</p>
            </div>

            <!-- Password -->
            <div class="form-group">
              <label for="password" class="form-label block text-left">Password</label>
              <div class="input-wrapper">
                <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" />
                  <path
                    d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <input :type="showPassword ? 'text' : 'password'" id="password" class="form-input"
                  placeholder="••••••••" v-model.trim="userProfile.password" :class="{ 'error-input': errors.password }"
                  @input="validatePasswordStrength">
                <button type="button" class="toggle-password" @click="showPassword = !showPassword">
                  <svg class="eye-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" stroke-width="2"
                      stroke-linecap="round" stroke-linejoin="round" />
                    <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </button>
              </div>

              <!-- Strength Meter -->
              <div v-if="userProfile.password" class="strength-meter">
                <div class="strength-bar">
                  <div class="strength-fill" :style="{ width: strengthPercent + '%', backgroundColor: strengthColor }">
                  </div>
                </div>
                <span class="strength-text" :style="{ color: strengthColor }">{{ strengthLabel }}</span>
              </div>

              <!-- Live policy checklist -->
              <ul v-if="userProfile.password" class="password-rules">
                <li :class="{ pass: passwordChecks().length }">
                  <span class="check-mark">{{ passwordChecks().length ? '✓' : '✗' }}</span>
                  At least {{ passwordPolicy.minLength }} characters
                </li>
                <li :class="{ pass: passwordChecks().uppercase }">
                  <span class="check-mark">{{ passwordChecks().uppercase ? '✓' : '✗' }}</span>
                  One uppercase letter
                </li>
                <li :class="{ pass: passwordChecks().number }">
                  <span class="check-mark">{{ passwordChecks().number ? '✓' : '✗' }}</span>
                  One number
                </li>
                <li :class="{ pass: passwordChecks().special }">
                  <span class="check-mark">{{ passwordChecks().special ? '✓' : '✗' }}</span>
                  One special character
                </li>
              </ul>

              <p v-if="errors.password" class="error-msg">{{ errors.password }}</p>
            </div>

            <!-- Confirm Password -->
            <div class="form-group">
              <label for="confirmPassword" class="form-label block text-left">Confirm Password</label>
              <div class="input-wrapper">
                <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" />
                  <path
                    d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <input type="password" id="confirmPassword" class="form-input" placeholder="••••••••"
                  v-model.trim="userProfile.confirmPassword" :class="{ 'error-input': errors.confirmPassword }">
              </div>
              <p v-if="errors.confirmPassword" class="error-msg">{{ errors.confirmPassword }}</p>
            </div>

            <!-- Terms & Privacy Consent (legal compliance — DPDP / GDPR) -->
            <div class="form-group terms-group">
              <label class="terms-label">
                <input type="checkbox" v-model="acceptedTerms" class="terms-checkbox" />
                <span>
                  I agree to the
                  <a href="/terms" target="_blank" rel="noopener">Terms of Service</a>
                  and
                  <a href="/privacy" target="_blank" rel="noopener">Privacy Policy</a>.
                </span>
              </label>
              <p v-if="errors.terms" class="error-msg">{{ errors.terms }}</p>
            </div>

            <button type="submit" class="btn-primary" :disabled="!otpVerified || !acceptedTerms">
              <span class="btn-text">Create Account</span>
              <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
                <path d="M12 5L19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </button>
          </form>

          <div class="text-center mt-6 text-gray-600">
            Already have an account? <a @click.prevent="routeLogin" href="#" class="signup-link-style">Sign In</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { notify } from '@/utils/notify';
export default {
  name: 'UserRegistration',


  data() {
    return {
      userProfile: {
        name: "",
        mobile: "",
        email: "",
        license: "",
        password: "",
        confirmPassword: ""
      },
      acceptedTerms: false,
      showConfirmPassword: false,

      // Password policy — single source of truth (used by validateForm + strength meter + UI hints)
      passwordPolicy: {
        minLength: 8,
        requireUppercase: true,
        requireNumber: true,
        requireSpecial: true,
      },
      errors: {},
      showPassword: false,

      // Password Strength Data
      strengthScore: 0,
      strengthLabel: '',
      strengthColor: '#e5e7eb',
      strengthPercent: 0,

      // OTP State
      otpSent: false,
      otpVerified: false,
      otpSending: false,
      otpVerifying: false,
      otpDigits: ['', '', '', '', '', ''],
      otpRefs: [],
      resendTimer: 0,
      resendInterval: null,
      otpAttempts: 0,
      maxOtpAttempts: 3,
      otpLocked: false,
      otpErrorType: null, // 'wrong', 'expired', 'max_attempts', 'rate_limit'
      resendCount: 0,
      maxResends: 3,

      // Country Code Picker
      countryId: 'IN',
      countryCode: '91',
      ccDropdownOpen: false,
      ccSearch: '',
      countryCodes: [
        { id: 'IN', code: '91', flag: '🇮🇳', name: 'India' },
        { code: '93', flag: '🇦🇫', name: 'Afghanistan' },
        { code: '355', flag: '🇦🇱', name: 'Albania' },
        { code: '213', flag: '🇩🇿', name: 'Algeria' },
        { code: '376', flag: '🇦🇩', name: 'Andorra' },
        { code: '244', flag: '🇦🇴', name: 'Angola' },
        { code: '54', flag: '🇦🇷', name: 'Argentina' },
        { code: '374', flag: '🇦🇲', name: 'Armenia' },
        { code: '61', flag: '🇦🇺', name: 'Australia' },
        { code: '43', flag: '🇦🇹', name: 'Austria' },
        { code: '994', flag: '🇦🇿', name: 'Azerbaijan' },
        { code: '973', flag: '🇧🇭', name: 'Bahrain' },
        { code: '880', flag: '🇧🇩', name: 'Bangladesh' },
        { code: '375', flag: '🇧🇾', name: 'Belarus' },
        { code: '32', flag: '🇧🇪', name: 'Belgium' },
        { code: '501', flag: '🇧🇿', name: 'Belize' },
        { code: '229', flag: '🇧🇯', name: 'Benin' },
        { code: '975', flag: '🇧🇹', name: 'Bhutan' },
        { code: '591', flag: '🇧🇴', name: 'Bolivia' },
        { code: '387', flag: '🇧🇦', name: 'Bosnia and Herzegovina' },
        { code: '267', flag: '🇧🇼', name: 'Botswana' },
        { code: '55', flag: '🇧🇷', name: 'Brazil' },
        { code: '673', flag: '🇧🇳', name: 'Brunei' },
        { code: '359', flag: '🇧🇬', name: 'Bulgaria' },
        { code: '226', flag: '🇧🇫', name: 'Burkina Faso' },
        { code: '257', flag: '🇧🇮', name: 'Burundi' },
        { code: '855', flag: '🇰🇭', name: 'Cambodia' },
        { code: '237', flag: '🇨🇲', name: 'Cameroon' },
        { id: 'CA', code: '1', flag: '🇨🇦', name: 'Canada' },
        { code: '238', flag: '🇨🇻', name: 'Cape Verde' },
        { code: '236', flag: '🇨🇫', name: 'Central African Republic' },
        { code: '235', flag: '🇹🇩', name: 'Chad' },
        { code: '56', flag: '🇨🇱', name: 'Chile' },
        { code: '86', flag: '🇨🇳', name: 'China' },
        { code: '57', flag: '🇨🇴', name: 'Colombia' },
        { code: '269', flag: '🇰🇲', name: 'Comoros' },
        { code: '242', flag: '🇨🇬', name: 'Congo' },
        { code: '506', flag: '🇨🇷', name: 'Costa Rica' },
        { code: '385', flag: '🇭🇷', name: 'Croatia' },
        { code: '53', flag: '🇨🇺', name: 'Cuba' },
        { code: '357', flag: '🇨🇾', name: 'Cyprus' },
        { code: '420', flag: '🇨🇿', name: 'Czech Republic' },
        { code: '45', flag: '🇩🇰', name: 'Denmark' },
        { code: '253', flag: '🇩🇯', name: 'Djibouti' },
        { code: '593', flag: '🇪🇨', name: 'Ecuador' },
        { code: '20', flag: '🇪🇬', name: 'Egypt' },
        { code: '503', flag: '🇸🇻', name: 'El Salvador' },
        { code: '240', flag: '🇬🇶', name: 'Equatorial Guinea' },
        { code: '291', flag: '🇪🇷', name: 'Eritrea' },
        { code: '372', flag: '🇪🇪', name: 'Estonia' },
        { code: '251', flag: '🇪🇹', name: 'Ethiopia' },
        { code: '679', flag: '🇫🇯', name: 'Fiji' },
        { code: '358', flag: '🇫🇮', name: 'Finland' },
        { code: '33', flag: '🇫🇷', name: 'France' },
        { code: '241', flag: '🇬🇦', name: 'Gabon' },
        { code: '220', flag: '🇬🇲', name: 'Gambia' },
        { code: '995', flag: '🇬🇪', name: 'Georgia' },
        { code: '49', flag: '🇩🇪', name: 'Germany' },
        { code: '233', flag: '🇬🇭', name: 'Ghana' },
        { code: '30', flag: '🇬🇷', name: 'Greece' },
        { code: '502', flag: '🇬🇹', name: 'Guatemala' },
        { code: '224', flag: '🇬🇳', name: 'Guinea' },
        { code: '245', flag: '🇬🇼', name: 'Guinea-Bissau' },
        { code: '592', flag: '🇬🇾', name: 'Guyana' },
        { code: '509', flag: '🇭🇹', name: 'Haiti' },
        { code: '504', flag: '🇭🇳', name: 'Honduras' },
        { code: '36', flag: '🇭🇺', name: 'Hungary' },
        { code: '354', flag: '🇮🇸', name: 'Iceland' },
        { code: '62', flag: '🇮🇩', name: 'Indonesia' },
        { code: '98', flag: '🇮🇷', name: 'Iran' },
        { code: '964', flag: '🇮🇶', name: 'Iraq' },
        { code: '353', flag: '🇮🇪', name: 'Ireland' },
        { code: '972', flag: '🇮🇱', name: 'Israel' },
        { code: '39', flag: '🇮🇹', name: 'Italy' },
        { code: '225', flag: '🇨🇮', name: 'Ivory Coast' },
        { code: '1876', flag: '🇯🇲', name: 'Jamaica' },
        { code: '81', flag: '🇯🇵', name: 'Japan' },
        { code: '962', flag: '🇯🇴', name: 'Jordan' },
        { id: 'KZ', code: '7', flag: '🇰🇿', name: 'Kazakhstan' },
        { code: '254', flag: '🇰🇪', name: 'Kenya' },
        { code: '686', flag: '🇰🇮', name: 'Kiribati' },
        { code: '965', flag: '🇰🇼', name: 'Kuwait' },
        { code: '996', flag: '🇰🇬', name: 'Kyrgyzstan' },
        { code: '856', flag: '🇱🇦', name: 'Laos' },
        { code: '371', flag: '🇱🇻', name: 'Latvia' },
        { code: '961', flag: '🇱🇧', name: 'Lebanon' },
        { code: '266', flag: '🇱🇸', name: 'Lesotho' },
        { code: '231', flag: '🇱🇷', name: 'Liberia' },
        { code: '218', flag: '🇱🇾', name: 'Libya' },
        { code: '423', flag: '🇱🇮', name: 'Liechtenstein' },
        { code: '370', flag: '🇱🇹', name: 'Lithuania' },
        { code: '352', flag: '🇱🇺', name: 'Luxembourg' },
        { code: '261', flag: '🇲🇬', name: 'Madagascar' },
        { code: '265', flag: '🇲🇼', name: 'Malawi' },
        { code: '60', flag: '🇲🇾', name: 'Malaysia' },
        { code: '960', flag: '🇲🇻', name: 'Maldives' },
        { code: '223', flag: '🇲🇱', name: 'Mali' },
        { code: '356', flag: '🇲🇹', name: 'Malta' },
        { code: '222', flag: '🇲🇷', name: 'Mauritania' },
        { code: '230', flag: '🇲🇺', name: 'Mauritius' },
        { code: '52', flag: '🇲🇽', name: 'Mexico' },
        { code: '373', flag: '🇲🇩', name: 'Moldova' },
        { code: '976', flag: '🇲🇳', name: 'Mongolia' },
        { code: '382', flag: '🇲🇪', name: 'Montenegro' },
        { code: '212', flag: '🇲🇦', name: 'Morocco' },
        { code: '258', flag: '🇲🇿', name: 'Mozambique' },
        { code: '95', flag: '🇲🇲', name: 'Myanmar' },
        { code: '264', flag: '🇳🇦', name: 'Namibia' },
        { code: '977', flag: '🇳🇵', name: 'Nepal' },
        { code: '31', flag: '🇳🇱', name: 'Netherlands' },
        { code: '64', flag: '🇳🇿', name: 'New Zealand' },
        { code: '505', flag: '🇳🇮', name: 'Nicaragua' },
        { code: '227', flag: '🇳🇪', name: 'Niger' },
        { code: '234', flag: '🇳🇬', name: 'Nigeria' },
        { code: '47', flag: '🇳🇴', name: 'Norway' },
        { code: '968', flag: '🇴🇲', name: 'Oman' },
        { code: '92', flag: '🇵🇰', name: 'Pakistan' },
        { code: '507', flag: '🇵🇦', name: 'Panama' },
        { code: '675', flag: '🇵🇬', name: 'Papua New Guinea' },
        { code: '595', flag: '🇵🇾', name: 'Paraguay' },
        { code: '51', flag: '🇵🇪', name: 'Peru' },
        { code: '63', flag: '🇵🇭', name: 'Philippines' },
        { code: '48', flag: '🇵🇱', name: 'Poland' },
        { code: '351', flag: '🇵🇹', name: 'Portugal' },
        { code: '974', flag: '🇶🇦', name: 'Qatar' },
        { code: '40', flag: '🇷🇴', name: 'Romania' },
        { id: 'RU', code: '7', flag: '🇷🇺', name: 'Russia' },
        { code: '250', flag: '🇷🇼', name: 'Rwanda' },
        { code: '966', flag: '🇸🇦', name: 'Saudi Arabia' },
        { code: '221', flag: '🇸🇳', name: 'Senegal' },
        { code: '381', flag: '🇷🇸', name: 'Serbia' },
        { code: '232', flag: '🇸🇱', name: 'Sierra Leone' },
        { code: '65', flag: '🇸🇬', name: 'Singapore' },
        { code: '421', flag: '🇸🇰', name: 'Slovakia' },
        { code: '386', flag: '🇸🇮', name: 'Slovenia' },
        { code: '252', flag: '🇸🇴', name: 'Somalia' },
        { code: '27', flag: '🇿🇦', name: 'South Africa' },
        { code: '82', flag: '🇰🇷', name: 'South Korea' },
        { code: '211', flag: '🇸🇸', name: 'South Sudan' },
        { code: '34', flag: '🇪🇸', name: 'Spain' },
        { code: '94', flag: '🇱🇰', name: 'Sri Lanka' },
        { code: '249', flag: '🇸🇩', name: 'Sudan' },
        { code: '597', flag: '🇸🇷', name: 'Suriname' },
        { code: '268', flag: '🇸🇿', name: 'Swaziland' },
        { code: '46', flag: '🇸🇪', name: 'Sweden' },
        { code: '41', flag: '🇨🇭', name: 'Switzerland' },
        { code: '963', flag: '🇸🇾', name: 'Syria' },
        { code: '886', flag: '🇹🇼', name: 'Taiwan' },
        { code: '992', flag: '🇹🇯', name: 'Tajikistan' },
        { code: '255', flag: '🇹🇿', name: 'Tanzania' },
        { code: '66', flag: '🇹🇭', name: 'Thailand' },
        { code: '228', flag: '🇹🇬', name: 'Togo' },
        { code: '676', flag: '🇹🇴', name: 'Tonga' },
        { code: '1868', flag: '🇹🇹', name: 'Trinidad and Tobago' },
        { code: '216', flag: '🇹🇳', name: 'Tunisia' },
        { code: '90', flag: '🇹🇷', name: 'Turkey' },
        { code: '993', flag: '🇹🇲', name: 'Turkmenistan' },
        { code: '256', flag: '🇺🇬', name: 'Uganda' },
        { code: '380', flag: '🇺🇦', name: 'Ukraine' },
        { code: '971', flag: '🇦🇪', name: 'United Arab Emirates' },
        { code: '44', flag: '🇬🇧', name: 'United Kingdom' },
        { id: 'US', code: '1', flag: '🇺🇸', name: 'United States' },
        { code: '598', flag: '🇺🇾', name: 'Uruguay' },
        { code: '998', flag: '🇺🇿', name: 'Uzbekistan' },
        { code: '678', flag: '🇻🇺', name: 'Vanuatu' },
        { code: '58', flag: '🇻🇪', name: 'Venezuela' },
        { code: '84', flag: '🇻🇳', name: 'Vietnam' },
        { code: '967', flag: '🇾🇪', name: 'Yemen' },
        { code: '260', flag: '🇿🇲', name: 'Zambia' },
        { code: '263', flag: '🇿🇼', name: 'Zimbabwe' },
      ]
    }

  },

  props: {
    headerVisiblity: Function,
  },

  directives: {
    clickOutside: {
      mounted(el, binding) {
        el._clickOutsideHandler = (e) => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener('click', el._clickOutsideHandler);
      },
      unmounted(el) {
        document.removeEventListener('click', el._clickOutsideHandler);
      }
    }
  },

  computed: {
    selectedCountry() {
      return this.countryCodes.find(c => c.id === this.countryId) || this.countryCodes[0];
    },
    filteredCountryCodes() {
      const q = this.ccSearch.trim().toLowerCase();
      if (!q) return this.countryCodes;
      return this.countryCodes.filter(c =>
        c.name.toLowerCase().includes(q) || c.code.includes(q)
      );
    },
    otpCode() {
      return this.otpDigits.join('');
    }
  },

  created() {
    // Keep the header visible on the signup page (same as the login page).
    this.headerVisiblity(true);
  },
  beforeUnmount() {
    if (this.resendInterval) clearInterval(this.resendInterval);
  },

  methods: {
    toggleCountryDropdown() {
      this.ccDropdownOpen = !this.ccDropdownOpen;
      if (this.ccDropdownOpen) {
        this.ccSearch = '';
        this.$nextTick(() => this.$refs.ccSearchInput?.focus());
      }
    },
    closeCountryDropdown() {
      this.ccDropdownOpen = false;
    },
    resetOtpState() {
      this.otpSent = false;
      this.otpVerified = false;
      this.otpDigits = ['', '', '', '', '', ''];
      this.otpAttempts = 0;
      this.otpLocked = false;
      this.otpErrorType = null;
      if (this.resendInterval) clearInterval(this.resendInterval);
      this.resendTimer = 0;
      this.resendCount = 0;
      this.errors.otp = '';
    },
    selectCountry(c) {
      this.countryId = c.id;
      this.countryCode = c.code;
      this.ccDropdownOpen = false;
      this.resetOtpState();
    },
    routeLogin: function () {
      this.$router.push({ name: 'LoginPage' });
    },
    passwordChecks() {
      const p = this.userProfile.password || '';
      return {
        length:    p.length >= this.passwordPolicy.minLength,
        uppercase: /[A-Z]/.test(p),
        number:    /[0-9]/.test(p),
        special:   /[^A-Za-z0-9]/.test(p),
      };
    },
    validatePasswordStrength() {
      const checks = this.passwordChecks();
      const score = Object.values(checks).filter(Boolean).length; // 0..4
      this.strengthScore = score;

      if (score <= 1) {
        this.strengthLabel = 'Weak';
        this.strengthColor = '#ef4444';
        this.strengthPercent = 25;
      } else if (score === 2) {
        this.strengthLabel = 'Fair';
        this.strengthColor = '#f59e0b';
        this.strengthPercent = 50;
      } else if (score === 3) {
        this.strengthLabel = 'Medium';
        this.strengthColor = '#eab308';
        this.strengthPercent = 75;
      } else {
        this.strengthLabel = 'Strong';
        this.strengthColor = '#22c55e';
        this.strengthPercent = 100;
      }
    },
    startResendTimer() {
      this.resendTimer = 60;
      if (this.resendInterval) clearInterval(this.resendInterval);
      this.resendInterval = setInterval(() => {
        this.resendTimer--;
        if (this.resendTimer <= 0) clearInterval(this.resendInterval);
      }, 1000);
    },
    onOtpDigitInput(index, e) {
      const val = e.target.value.replace(/[^0-9]/g, '');
      this.otpDigits[index] = val.slice(-1);
      this.errors.otp = '';
      if (val && index < 5) {
        this.$nextTick(() => this.otpRefs[index + 1]?.focus());
      }
    },
    onOtpKeydown(index, e) {
      if (e.key === 'Backspace') {
        if (!this.otpDigits[index] && index > 0) {
          this.otpDigits[index - 1] = '';
          this.$nextTick(() => this.otpRefs[index - 1]?.focus());
        } else {
          this.otpDigits[index] = '';
        }
      }
    },
    onOtpPaste(e) {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').slice(0, 6);
      text.split('').forEach((d, i) => { this.otpDigits[i] = d; });
      const nextFocus = Math.min(text.length, 5);
      this.$nextTick(() => this.otpRefs[nextFocus]?.focus());
    },
    async sendOtp() {
      this.errors.mobile = '';
      if (this.userProfile.mobile.length < 7) {
        this.errors.mobile = "Enter a valid mobile number.";
        return;
      }
      this.otpSending = true;
      this.otpDigits = ['', '', '', '', '', ''];
      this.otpAttempts = 0;
      this.otpLocked = false;
      this.otpErrorType = null;
      try {
        await axios.post(this.$API_URL + "/whatsapp/send-registration-otp", {
          phone_number: this.userProfile.mobile,
          country_code: this.countryCode,
          country_id: this.countryId,
        });
        this.otpSent = true;
        this.resendCount++;
        this.startResendTimer();
        this.$nextTick(() => this.otpRefs[0]?.focus());
      } catch (error) {
        const msg = error.response?.data?.detail || "Failed to send OTP. Try again.";
        const status = error.response?.status;
        if (status === 429) {
          this.errors.mobile = "Too many OTP requests. Please wait 10 minutes.";
        } else {
          this.errors.mobile = msg;
        }
      } finally {
        this.otpSending = false;
      }
    },
    async verifyOtp() {
      this.errors.otp = '';
      if (this.otpCode.length !== 6) {
        this.errors.otp = "Enter the 6-digit OTP from WhatsApp.";
        return;
      }
      this.otpVerifying = true;
      try {
        await axios.post(this.$API_URL + "/whatsapp/verify-registration-otp", {
          phone_number: this.userProfile.mobile,
          country_code: this.countryCode,
          country_id: this.countryId,
          otp_code: this.otpCode,
        });
        this.otpVerified = true;
      } catch (error) {
        const msg = error.response?.data?.detail || "Invalid OTP. Please check and try again.";

        // Parse attempts remaining from error message (backend: "Invalid OTP. 2 attempt(s) remaining.")
        const attemptsMatch = msg.match(/(\d+)\s+attempt/);
        if (attemptsMatch) {
          const remaining = parseInt(attemptsMatch[1]);
          this.otpAttempts = this.maxOtpAttempts - remaining;

          if (remaining === 0) {
            this.otpLocked = true;
            this.otpErrorType = 'max_attempts';
          }
        }

        // Determine error type
        if (msg.includes("expired")) {
          this.otpErrorType = 'expired';
        } else if (msg.includes("Maximum attempts")) {
          this.otpLocked = true;
          this.otpErrorType = 'max_attempts';
        }

        this.errors.otp = msg;
        this.otpDigits = ['', '', '', '', '', ''];
        if (!this.otpLocked) {
          this.$nextTick(() => this.otpRefs[0]?.focus());
        }
      } finally {
        this.otpVerifying = false;
      }
    },
    scrollToFirstError() {
      this.$nextTick(() => {
        const el = document.querySelector('.error-input, .error-msg');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const input = el.closest('.form-group')?.querySelector('input') || el;
          input.focus?.();
        }
      });
    },

    registerUser: function () {

      if (!this.validateForm()) {
        this.scrollToFirstError();
        return;
      }

      if (!this.otpVerified) {
        this.errors.mobile = "Please verify your mobile number first.";
        this.scrollToFirstError();
        return;
      }

      const payload = {
        ...this.userProfile,
        otp_code: this.otpCode,
        country_code: this.countryCode,
        country_id: this.countryId,
        terms_accepted: this.acceptedTerms,
      };
      axios.post(this.$API_URL + "/user/register", JSON.stringify(payload), {
        headers: { 'Content-Type': 'application/json' }
      })
        .then((response) => {
          console.log(response.data);
          this.routeLogin();
        })
        .catch((error) => {
          const detail = error.response?.data?.detail || error.response?.data?.message;
          notify.error(typeof detail === 'string' ? detail : "Couldn’t create your account. Check your details and try again.");
          console.error(error);
        });
    },
    validateForm() {
      this.errors = {};

      // REQUIRED FIELDS
      if (!this.userProfile.name.trim()) this.errors.name = "Name is required";
      if (!this.userProfile.email.trim()) this.errors.email = "Email is required";
      if (!this.userProfile.mobile.trim()) this.errors.mobile = "Mobile number is required";
      if (!this.userProfile.license.trim()) this.errors.license = "License code is required";
      if (!this.userProfile.password.trim()) this.errors.password = "Password is required";
      if (!this.userProfile.confirmPassword.trim()) this.errors.confirmPassword = "Confirm Password is required";

      // EMAIL FORMAT VALIDATION
      if (
        this.userProfile.email.trim() &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.userProfile.email)
      ) {
        this.errors.email = "Invalid email format";
      }

      // MOBILE NUMBER VALIDATION (multi-country via libphonenumber)
      if (this.userProfile.mobile.trim()) {
        try {
          if (!isValidPhoneNumber('+' + this.countryCode + this.userProfile.mobile, this.countryId)) {
            this.errors.mobile = `Invalid mobile number for ${this.selectedCountry.name}`;
          }
        } catch {
          this.errors.mobile = "Invalid mobile number";
        }
      }

      // PASSWORD POLICY (single source of truth — same rules as strength meter)
      if (this.userProfile.password.trim()) {
        const checks = this.passwordChecks();
        const failed = [];
        if (!checks.length)    failed.push(`at least ${this.passwordPolicy.minLength} characters`);
        if (!checks.uppercase) failed.push("an uppercase letter");
        if (!checks.number)    failed.push("a number");
        if (!checks.special)   failed.push("a special character");
        if (failed.length) {
          this.errors.password = "Password must include " + failed.join(", ") + ".";
        }
      }

      // CONFIRM PASSWORD MATCH
      if (
        this.userProfile.password.trim() &&
        this.userProfile.confirmPassword.trim() &&
        this.userProfile.password !== this.userProfile.confirmPassword
      ) {
        this.errors.confirmPassword = "Passwords do not match";
      }

      // TERMS & PRIVACY CONSENT
      if (!this.acceptedTerms) {
        this.errors.terms = "You must accept the Terms of Service and Privacy Policy.";
      }

      return Object.keys(this.errors).length === 0;
    }


  }
}
</script>

<style scoped>
/* With the header visible, fill the space under it instead of forcing
   100vh (same fix as the login page). */
.login-body-polished {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.login-body-polished .container {
  height: 100%;
  flex: 1;
  min-height: 0;
}

/* Password Strength Meter */
.strength-meter {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.strength-bar {
  flex-grow: 1;
  height: 6px;
  background-color: #f1f5f9;
  /* slate-100 */
  border-radius: 4px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  transition: width 0.3s ease, background-color 0.3s ease;
}

.strength-text {
  font-size: 0.8rem;
  font-weight: 600;
  min-width: 50px;
  text-align: right;
}

/* ── Mobile Row ── */
.mobile-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.mobile-input {
  height: 42px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #1e293b;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
}

.mobile-input:focus {
  border-color: #0071C5;
}

.mobile-input:disabled {
  background: #f8fafc;
  color: #64748b;
  cursor: not-allowed;
}

.mobile-input.error-input {
  border-color: #ef4444;
}

/* ── Country Code Picker ── */
.cc-picker {
  position: relative;
  flex-shrink: 0;
}

.cc-trigger {
  display: flex;
  align-items: center;
  gap: 5px;
  height: 42px;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  cursor: pointer;
  font-size: 0.88rem;
  color: #1e293b;
  white-space: nowrap;
  transition: border-color 0.2s, background 0.2s;
  min-width: 90px;
}

.cc-trigger:hover:not(:disabled) {
  border-color: #0071C5;
  background: #eff6ff;
}

.cc-trigger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cc-flag {
  font-size: 1.2rem;
  line-height: 1;
}

.cc-code {
  font-weight: 600;
  font-size: 0.85rem;
}

.cc-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 999;
  width: 260px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.cc-search-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  background: #f8fafc;
}

.cc-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.875rem;
  color: #1e293b;
}

.cc-search::placeholder {
  color: #64748b;
}

.cc-list {
  list-style: none;
  margin: 0;
  padding: 4px 0;
  max-height: 250px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.cc-list::-webkit-scrollbar {
  width: 4px;
}

.cc-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.cc-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  cursor: pointer;
  transition: background 0.15s;
  font-size: 0.875rem;
}

.cc-item:hover {
  background: #f0f9ff;
}

.cc-item.active {
  background: #eff6ff;
}

.cc-item-flag {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.cc-item-name {
  flex: 1;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cc-item-code {
  color: #64748b;
  font-size: 0.8rem;
  flex-shrink: 0;
}

.cc-empty {
  padding: 16px;
  text-align: center;
  color: #64748b;
  font-size: 0.875rem;
}

.otp-send-btn {
  flex-shrink: 0;
  padding: 0 14px;
  height: 42px;
  background: #0071C5;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
}

.otp-send-btn:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.otp-send-btn:not(:disabled):hover {
  background: #005fa3;
}

.otp-hint {
  font-size: 0.82rem;
  color: #64748b;
  margin: 0 0 10px 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
}

.otp-boxes-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.otp-box {
  width: 44px;
  height: 48px;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  color: #1e293b;
  background: #f8fafc;
  transition: border-color 0.2s, background 0.2s, opacity 0.2s;
  flex-shrink: 0;
}

.otp-box:focus {
  border-color: #0071C5;
  background: #eff6ff;
}

.otp-box:disabled {
  background: #f1f5f9;
  color: #64748b;
  cursor: not-allowed;
  opacity: 0.6;
}

.otp-box.error-input {
  border-color: #ef4444;
  background: #fff5f5;
}

.otp-box.error-input:disabled {
  border-color: #dc2626;
  background: #fee2e2;
}

/* Mobile Input Wrapper */
.mobile-input-wrapper {
  position: relative;
  flex: 1;
  min-width: 0;
}

.mobile-input {
  width: 100%;
}

.verified-badge-circle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background: #25D366;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.1);
}

.mobile-input.verified {
  padding-right: 48px;
}

/* Checkmark Pop Animation + Pulse */
.badge-pop-enter-active {
  animation: badge-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), badge-pulse 0.6s ease-out 0.5s;
}

.badge-pop-leave-active {
  animation: badge-pop 0.3s reverse;
}

@keyframes badge-pop {
  from {
    opacity: 0;
    transform: translateY(-50%) scale(0.3);
  }

  to {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
}

@keyframes badge-pulse {
  0% {
    box-shadow: 0 3px 10px rgba(37, 211, 102, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.1);
  }

  50% {
    box-shadow: 0 3px 10px rgba(37, 211, 102, 0.6), 0 0 0 6px rgba(37, 211, 102, 0.2);
  }

  100% {
    box-shadow: 0 3px 10px rgba(37, 211, 102, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.1);
  }
}

/* Change Button */
.change-btn {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #0071C5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.change-btn:hover {
  border-color: #0071C5;
  background: #eff6ff;
  color: #005fa3;
}

.password-rules {
  list-style: none;
  padding: 8px 0 0 0;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 16px;
}
.password-rules li {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: #ef4444;
}
.password-rules li.pass {
  color: #16a34a;
}
.password-rules .check-mark {
  font-weight: 700;
  width: 12px;
  display: inline-block;
  text-align: center;
}

.terms-group {
  margin: 4px 0 12px 0;
}
.terms-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.85rem;
  color: #475569;
  cursor: pointer;
  line-height: 1.4;
}
.terms-checkbox {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: #0071C5;
  cursor: pointer;
}
.terms-label a {
  color: #0071C5;
  text-decoration: underline;
  font-weight: 600;
}

.spinner-sm-dark {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
